import {
    createEntityAdapter,
    createSelector,
    createSlice,
    EntityState,
    PayloadAction,
} from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { IWorkTimeType } from "../../../models/workShiftTimeSlot/workTimeType";
import {
    getWorkTimeTypes,
    startWorkTime,
    getWorkShiftTimeSlotItems,
    getWorkShiftTimeSlotItem,
    saveWorkTime,
    getSalaryRowTypes,
    WorkShiftTimeSlotStartResult,
    overwritingBreakSave,
    saveWorkTimeComment,
} from "../../../services/workShiftTimeSlotService";
import { IWorkShiftTimeSlot } from "../../../models/workShiftTimeSlot/workShiftTimeSlot";
import { IWorkShiftTimeSlotItems } from "../../../models/workShiftTimeSlot/workShiftTimeSlotItems";
import { Base } from "../../common/base";
import { IWorkTimeStartFormItem } from "../../../models/workShiftTimeSlot/workTimeStartFormItem";
import { IWorkTimeSaveData } from "../../../models/workShiftTimeSlot/workTimeSaveData";
import { ISalaryRowType } from "../../../models/salaryRowType/salaryRowType";
import { NonOverlappingWorkShiftTimeSlotSaveData } from "../../../models/workShiftTimeSlot/nonOverlappingWorkShiftTimeSlotSaveData";
import { IWorkTimeCommentSaveData } from "../../../models/workShiftTimeSlot/workTimeCommentSaveData";

export interface WorkTimeState {
    workTimeTypes: IWorkTimeType[];
    currentId: string;
    salaryRowTypes: ISalaryRowType[];
    formItem: IWorkTimeStartFormItem;
}

const initialState: WorkTimeState = {
    workTimeTypes: null,
    salaryRowTypes: null,
    currentId: null,
    formItem: null,
};

const sortComparer = (a, b) =>
    Base.dateCompare(a.startDate, b.startDate) ||
    Base.dateCompare(a.endDate, b.endDate);

const workShiftTimeSlotAdapter = createEntityAdapter<IWorkShiftTimeSlot>({
    sortComparer,
});

export const workShiftTimeSlotSlice = createSlice({
    name: "workShiftTimeSlot",
    initialState: workShiftTimeSlotAdapter.getInitialState(initialState),
    reducers: {
        setCurrentWorkShiftTimeSlotId: (
            state,
            action: PayloadAction<string>
        ) => {
            state.currentId = action.payload;
        },
        setWorkTimeFormItem: (
            state,
            action: PayloadAction<IWorkTimeStartFormItem>
        ) => {
            state.formItem = action.payload;
        },
        setWorkTimeTypes: (state, action: PayloadAction<IWorkTimeType[]>) => {
            state.workTimeTypes = action.payload;
        },
        setSalaryRowTypes: (
            state,
            action: PayloadAction<ISalaryRowType[]>
        ) => {
            state.salaryRowTypes = action.payload;
        },
        workShiftTimeSlotsReceived: (
            state,
            action: PayloadAction<IWorkShiftTimeSlotItems>
        ) => {
            workShiftTimeSlotAdapter.upsertMany(state, action.payload.items);
        },
        workShiftTimeSlotReceived: (
            state,
            action: PayloadAction<IWorkShiftTimeSlot>
        ) => {
            if (action.payload)
                workShiftTimeSlotAdapter.upsertOne(state, action.payload);
        },
        removeWorkShiftTimeSlot: (state, action: PayloadAction<string>) => {
            workShiftTimeSlotAdapter.removeOne(state, action.payload);
        },
    },
});

export const {
    setCurrentWorkShiftTimeSlotId,
    setWorkTimeFormItem,
    setWorkTimeTypes,
    workShiftTimeSlotsReceived,
    setSalaryRowTypes,
    workShiftTimeSlotReceived,
    removeWorkShiftTimeSlot,
} = workShiftTimeSlotSlice.actions;

export const saveStartWorkTime = createAsyncThunk<WorkShiftTimeSlotStartResult, IWorkTimeStartFormItem>(
    "workShiftTimeSlot/start",
    async(formData: IWorkTimeStartFormItem, thunkApi): Promise<WorkShiftTimeSlotStartResult> => {
        const result = await startWorkTime(formData);
        thunkApi.dispatch(workShiftTimeSlotReceived(result.newSlot));
        thunkApi.dispatch(setCurrentWorkShiftTimeSlotId(result.newSlot?.id));
        thunkApi.dispatch(setWorkTimeFormItem(null));
        return result;
    }
);

export const fetchWorkTimeTypes = createAsyncThunk(
    "workShiftTimeSlot/workTimeTypes",
    async(_, thunkApi) => {
        const workTimeTypes = await getWorkTimeTypes(thunkApi.signal);
        thunkApi.dispatch(setWorkTimeTypes(workTimeTypes));
    }
);

export const fetchSalaryRowTypes = createAsyncThunk(
    "workShiftTimeSlot/salaryRowTypes",
    async(_, thunkApi) => {
        const salaryRowTypes = await getSalaryRowTypes(thunkApi.signal);
        thunkApi.dispatch(setSalaryRowTypes(salaryRowTypes));
    }
);

export const fetchWorkShiftTimeSlotItems = createAsyncThunk(
    "workShiftTimeSlot/list",
    async(
        { startDate, endDate }: { startDate: string; endDate: string },
        thunkApi
    ) => {
        const workShiftTimeSlotItems = await getWorkShiftTimeSlotItems(
            startDate,
            endDate,
            thunkApi.signal
        );
        thunkApi.dispatch(workShiftTimeSlotsReceived(workShiftTimeSlotItems));
    }
);

export const fetchWorkShiftTimeSlot = createAsyncThunk(
    "workshifttimeslot/get",
    async({ id }: { id: string }, thunkApi) => {
        const workShiftTimeSlot = await getWorkShiftTimeSlotItem(
            id,
            thunkApi.signal
        );
        thunkApi.dispatch(workShiftTimeSlotReceived(workShiftTimeSlot));
    }
);

export const saveWorkShiftTimeSlot = createAsyncThunk(
    "workshifttimeslot/save",
    async(data: IWorkTimeSaveData, thunkApi) => {
        const workShiftTimeSlot = await saveWorkTime(data);
        thunkApi.dispatch(workShiftTimeSlotReceived(workShiftTimeSlot));
    }
);

export const saveWorkShiftComment = createAsyncThunk(
    "workshift/comment/save",
    async(data: IWorkTimeCommentSaveData, thunkApi) => {
        const workShiftTimeSlot = await saveWorkTimeComment(data);
        thunkApi.dispatch(workShiftTimeSlotReceived(workShiftTimeSlot));
    }
);

export const saveOverwritingBreak = createAsyncThunk(
    "overwritingBreak/save",
    async(data: NonOverlappingWorkShiftTimeSlotSaveData, thunkApi) => {
        const workShiftTimeSlot = await overwritingBreakSave(data);
        thunkApi.dispatch(workShiftTimeSlotReceived(workShiftTimeSlot));
    }
);

export const workShiftTimeSlotReducer = workShiftTimeSlotSlice.reducer;

export const workShiftTimeSlotSelector =
    workShiftTimeSlotAdapter.getSelectors();

export const makeSelectWorkShiftTimeSlotsByIds = () =>
    createSelector(
        (state: EntityState<IWorkShiftTimeSlot, string> & WorkTimeState) =>
            workShiftTimeSlotSelector.selectEntities(state),
        (_, ids: string[]) => ids,
        (dict: Record<string, IWorkShiftTimeSlot>, ids: string[]) =>
            Base.getUniqueStringItems(ids ?? [])
                .map((id) => dict[id])
                .filter(Boolean)
                .sort(sortComparer)
    );
